import React, { useCallback, useEffect, useState } from "react";
import { Box, Typography, Grid, Chip, Paper, alpha, useTheme, Divider, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import DescriptionIcon from "@mui/icons-material/Description";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import AddIcon from "@mui/icons-material/Add";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { RootState, useAppDispatch } from "../../../../app/features/store/store";
import { getKnowledgehubList } from "../../../../app/features/knowledgehub/knowledgehubSlice";
import getAxiosError from "../../../../app/api/axiosError";
import Loader from "../../loader/Loader";
import { KnowledgehubFolderResponse, KnowledgehubFileResponse, ArchiveList } from "../../../../app/models/responseModels/knowledgehubResponse";
import KnowledgehubHeader, { BreadcrumbItem } from "../components/KnowledgehubHeader";
import KnowledgehubFolderCard from "../components/KnowledgehubFolderCard";
import KnowledgehubFileCard from "../components/KnowledgehubFileCard";
import KnowledgehubFileViewer from "../components/KnowledgehubFileViewer";
import KnowledgehubCreateRepoModal from "../components/KnowledgehubCreateRepoModal";
import KnowledgehubUploadFileModal, { UploadFilePayload } from "../components/KnowledgehubUploadFileModal";
import KnowledgehubEditDrawer, { EditTarget } from "../components/KnowledgehubEditDrawer";
import KnowledgehubArchiveDrawer from "../components/KnowledgehubArchiveDrawer";
import { CreateKnowledgehubRepoRequest } from "../../../../app/models/requestModels/knowledgehubRequest";
import { KnowledgehubAgent } from "../../../../app/api/knowledgehub/KnowledgehubAgent";
import { PAGES } from "../../../../enums/PagesEnum";

export default function KnowledgehubPage() {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const { isLoading, knowledgehubList } = useSelector(
    (state: RootState) => state.knowledgehub
  );

  const [currentFolder, setCurrentFolder] = useState<KnowledgehubFolderResponse | null>(null);
  const [history, setHistory] = useState<(KnowledgehubFolderResponse | null)[]>([]);
  const [hoveredItemId, setHoveredItemId] = useState<number | null>(null);
  const [viewerFile, setViewerFile] = useState<KnowledgehubFileResponse | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [uploadTargetFolder, setUploadTargetFolder] = useState<KnowledgehubFolderResponse | null>(null);
  const [editTarget, setEditTarget] = useState<EditTarget | null>(null);
  const [archiveDrawerOpen, setArchiveDrawerOpen] = useState(false);
  const [archivedFiles, setArchivedFiles] = useState<ArchiveList[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'folder' | 'file'; data: KnowledgehubFolderResponse | KnowledgehubFileResponse } | null>(null);
  const [isPageFrozen, setIsPageFrozen] = useState(false);

  // ***** Get page Permissions *****
  const pagePermission = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings?.pagePermissions.find(
      (x) => x.pageName === PAGES.KNOWLEDGE_HUB
    )
  );

  const IsCreatePermission = pagePermission
    ? pagePermission.isCreatePermission
    : false;

  const IsAdmin = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings
      ? state.login?.authResponse?.userSettings?.roles.includes(
        "Administrators"
      )
      : false
  );

  const handleCreateRepo = async (request: CreateKnowledgehubRepoRequest) => {
    // Create the repository object without the files
    setLoading(true)
    const createRepoRequest = {
      name: request.name,
      description: request.description,
      parentId: request.parentId,
      fdpRoleIds: request.fdpRoleIds,
      fdcRoleIds: request.fdcRoleIds,
      manualEmails: request.manualEmails,
      allUsers: request.allUsers,
    };

    const formData = new FormData();
    formData.append("repository", new Blob([JSON.stringify(createRepoRequest)], { type: "application/json" }));

    if (request.files && request.files.length > 0) {
      request.files.forEach((file) => {
        formData.append("files", file);
      });
    }

    try {
      const result = await KnowledgehubAgent.createRepoWithFiles(formData);
      if (!result.isSuccess) {
        toast.error(result.message || "Failed to create repository");
        return false;
      }
      toast.success("Repository created successfully", { toastId: "createRepoSuccess" });
      await fetchData();
      return true;
    } catch (error: any) {
      const msg = error?.message?.message || error?.message || "Upload failed.";
      setIsPageFrozen(true);
      toast.error(msg, {
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        onClose: () => setIsPageFrozen(false),
      });
      return false;
    } finally {
      setLoading(false)
    }
  };

  const handleUploadFile = async (repositoryId: number, payload: UploadFilePayload) => {
    setLoading(true)

    const UploadFilePayloadReq = {
      description: payload.description,
      fdpRoleIds: payload.fdpRoleIds,
      fdcRoleIds: payload.fdcRoleIds,
      manualEmails: payload.manualEmails,
      allUsers: payload.allUsers
    };

    const formData = new FormData();
    formData.append("metadata", new Blob([JSON.stringify(UploadFilePayloadReq)], { type: "application/json" }));

    if (payload.files && payload.files.length > 0) {
      payload.files.forEach((file) => {
        formData.append("files", file);
      });
    }

    try {
      await KnowledgehubAgent.directFileUpload(repositoryId, formData);
      toast.success("Files are uploaded successfully");
      await fetchData();
      return true;
    } catch (error: any) {
      const msg = error?.message?.message || error?.message || "Upload failed.";
      setIsPageFrozen(true);
      toast.error(msg, {
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        onClose: () => setIsPageFrozen(false),
      });
      return false;
    } finally {
      setLoading(false)
    }
  };

  const handleDeleteFolder = async (folder: KnowledgehubFolderResponse) => {
    setDeleteTarget({ type: 'folder', data: folder });
    setDeleteConfirmOpen(true);
  };

  const handleDeleteFile = async (file: KnowledgehubFileResponse) => {
    setDeleteTarget({ type: 'file', data: file });
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    setLoading(true);
    try {
      if (deleteTarget.type === 'folder') {
        const folder = deleteTarget.data as KnowledgehubFolderResponse;
        await KnowledgehubAgent.softDeleteSubmittedRepository(folder.id);
        toast.success("Repository deleted successfully");
      } else {
        const file = deleteTarget.data as KnowledgehubFileResponse;
        await KnowledgehubAgent.softDeleteSubmittedFile(file.id);
        toast.success(`"${file.title}" deleted successfully`);
      }
      await fetchData();
    } catch (error: any) {
      const msg = error?.message?.message || error?.message || "Delete failed.";
      toast.error(msg);
    } finally {
      setLoading(false);
      setDeleteConfirmOpen(false);
      setDeleteTarget(null);
    }
  };

  const handleEditFolder = (folder: KnowledgehubFolderResponse) => {
    setEditTarget({ type: "folder", data: folder });
  };
  const handleEditFile = (file: KnowledgehubFileResponse) => {
    setEditTarget({ type: "file", data: file });
  };

  const handleArchiveFile = async (file: KnowledgehubFileResponse) => {
    setLoading(true);
    try {
      const response = await KnowledgehubAgent.getArchiveList(file.id);
      if (response.isSuccess && response.data) {
        setArchivedFiles(response.data);
        setArchiveDrawerOpen(true);
      } else {
        toast.error("Failed to fetch archived files");
      }
    } catch (error: any) {
      const msg = error?.message || "Failed to fetch archived files";
      toast.error(msg)
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadArchivedFile = async (file: ArchiveList) => {
    setLoading(true);
    try {
      const response = await KnowledgehubAgent.downloadArchivedFile(file.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const title = file.storagePath?.split("/").pop() ?? "";
      link.setAttribute("download", title || "download");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(`"${title}" downloaded successfully`);
    } catch (error: any) {
      const msg = error?.message || "Failed to download archived file";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleEditSave = async (target: EditTarget, updated: any) => {
    setLoading(true);
    try {
      if (target.type === "folder") {
        const payload = {
          name: updated.name,
          description: updated.description,
          parentId: updated.parentId,
          fdpRoleIds: [],
          fdcRoleIds: [],
          manualEmails: [],
          allUsers: updated.allUsers
        };
        const response = await KnowledgehubAgent.editRepository(updated.id, payload);
        if (response.isSuccess) {
          toast.success("Repository updated successfully");
          await fetchData();
        } else {
          toast.error(response.message || "Failed to update repository");
        }
      } else {
        const payload = {
          description: updated.description,
          fdpRoleIds: updated.fdpRoleIds,
          fdcRoleIds: updated.fdcRoleIds,
          manualEmails: updated.manualEmails,
          allUsers: updated.allUsers
        };
        const response = await KnowledgehubAgent.editDocument(updated.id, payload);
        if (response.isSuccess) {
          toast.success("Changes saved successfully");
          await fetchData();
        } else {
          toast.error(response.message || "Failed to update document");
        }
      }
    } catch (error: any) {
      const msg = error?.message || "Failed to update";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleReuploadFile = async (
    file: KnowledgehubFileResponse,
    newFile: File
  ) => {
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("file", newFile);

      const response = await KnowledgehubAgent.reUploadFile(file.id, formData);

      if (response?.isSuccess) {
        toast.success(`"${newFile.name}" updated successfully`);
        await fetchData(); // refresh list
      } else {
        toast.error(response?.message || "Reupload failed");
      }

    } catch (error: any) {
      const msg = error?.message || "Reupload failed";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadFile = async (file: KnowledgehubFileResponse) => {
    try {
      setLoading(true);
      const response = await KnowledgehubAgent.downloadDocument(file.id);

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", file.title || "download");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`"${file.title}" downloaded successfully`);
    } catch (error) {
      getAxiosError(error);
      toast.error("File isn't accessible.", {
        toastId: "FetchFdpInvoiceFileException",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const response = await dispatch(getKnowledgehubList()).unwrap();

      // CRITICAL FIX: Re-sync currentFolder after data refresh
      if (currentFolder) {
        const updatedFolder = findFolderById(response, currentFolder.id);
        if (updatedFolder) {
          setCurrentFolder(updatedFolder);
        }
      }
    } catch (error) {
      getAxiosError(error);
    }
  }, [dispatch, currentFolder]);

  // Helper function to find folder by ID in nested structure
  const findFolderById = (
    folders: KnowledgehubFolderResponse[],
    id: number
  ): KnowledgehubFolderResponse | null => {
    for (const folder of folders) {
      if (folder.id === id) return folder;
      if (folder.children && folder.children.length > 0) {
        const found = findFolderById(folder.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  useEffect(() => {
    fetchData();
  }, []);


  const folders: KnowledgehubFolderResponse[] = currentFolder ? currentFolder.children : knowledgehubList;
  const files: KnowledgehubFileResponse[] = currentFolder ? currentFolder.files ?? [] : [];

  const openFolder = (folder: KnowledgehubFolderResponse) => {
    setHistory([...history, currentFolder]);
    setCurrentFolder(folder);
  };

  const goBack = () => {
    setCurrentFolder(history[history.length - 1] ?? null);
    setHistory(history.slice(0, -1));
  };

  const goHome = () => {
    setCurrentFolder(null);
    setHistory([]);
  };

  const getBreadcrumbPath = (): BreadcrumbItem[] => {
    const sanitizeName = (name: string) => {
      // XSS Protection: Sanitize folder names
      return name.replace(/[<>"'&]/g, (char) => {
        const entities: { [key: string]: string } = {
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#39;',
          '&': '&amp;'
        };
        return entities[char] || char;
      });
    };

    const path: { id: number | null; name: string; onClick: () => void }[] = [
      { id: null, name: "Home", onClick: goHome },
    ];
    const historyStack = [...history];
    for (let i = 0; i < historyStack.length; i++) {
      const folder = historyStack[i];
      if (folder) {
        path.push({
          id: folder.id,
          name: sanitizeName(folder.name),
          onClick: () => {
            setCurrentFolder(folder);
            setHistory(historyStack.slice(0, i));
          },
        });
      }
    }
    if (currentFolder) {
      path.push({ id: currentFolder.id, name: sanitizeName(currentFolder.name), onClick: () => { } });
    }
    return path;
  };

  return (
    <Box sx={{ width: "100%", position: "relative" }}>
      {/* Freeze Overlay */}
      {isPageFrozen && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 9998,
            cursor: "not-allowed",
          }}
        />
      )}

      {/* Header + Breadcrumb */}
      <KnowledgehubHeader
        folders={folders}
        files={files}
        breadcrumbPath={getBreadcrumbPath()}
        showBack={history.length > 0}
        onBack={goBack}
        onRefresh={fetchData}
      />

      {/* Content */}
      {isLoading ? (
        <Loader isLoading={isLoading} />
      ) : (
        <Box sx={{ p: 3 }}>
          {/* Action Buttons */}
          {IsCreatePermission && IsAdmin && (<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mb: 2 }}>
            {currentFolder && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<UploadFileIcon />}
                onClick={() => setUploadTargetFolder(currentFolder)}
              >
                Upload File
              </Button>
            )}
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setIsCreateModalOpen(true)}
            >
              {currentFolder ? "Create Sub-Repository" : "Create Repository"}
            </Button>
          </Box>)}

          {folders.length === 0 && files.length === 0 ? (

            // Empty State
            <Paper
              sx={{
                p: 6,
                textAlign: "center",
                bgcolor: "white",
                borderRadius: 3,
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
              }}
            >
              <FolderIcon sx={{ fontSize: 64, color: alpha(theme.palette.text.disabled, 0.5), mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                This Repository is empty
              </Typography>
              <Typography variant="body2" color="text.disabled">
                No Repository or files found in this location
              </Typography>
            </Paper>

          ) : (
            <>
              {/* REPOSITORIES */}
              {folders.length > 0 && (
                <>
                  <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                    <FolderOpenIcon sx={{ color: theme.palette.primary.main }} />
                    <Typography variant="h6" fontWeight={600}>Repositories</Typography>
                    <Chip
                      label={folders.length}
                      size="small"
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                        fontWeight: 500,
                      }}
                    />
                  </Box>
                  <Grid container spacing={2.5} sx={{ mb: 4 }}>
                    {folders.map((folder, index) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={folder.id}>
                        <KnowledgehubFolderCard
                          folder={folder}
                          index={index}
                          isHovered={hoveredItemId === folder.id}
                          onMouseEnter={() => setHoveredItemId(folder.id)}
                          onMouseLeave={() => setHoveredItemId(null)}
                          onClick={() => openFolder(folder)}
                          onDelete={handleDeleteFolder}
                          onUpload={(f) => setUploadTargetFolder(f)}
                          onEdit={handleEditFolder}
                        />
                      </Grid>
                    ))}
                  </Grid>

                  {files.length > 0 && (
                    <Divider sx={{ my: 4 }}>
                      <Chip
                        label="DOCUMENTS"
                        size="small"
                        sx={{
                          fontWeight: 600,
                          letterSpacing: "0.5px",
                          bgcolor: alpha(theme.palette.text.secondary, 0.08),
                        }}
                      />
                    </Divider>
                  )}
                </>
              )}

              {/* FILES */}
              {files.length > 0 && (
                <>
                  {folders.length === 0 && (
                    <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                      <DescriptionIcon sx={{ color: theme.palette.info.main }} />
                      <Typography variant="h6" fontWeight={600}>Files</Typography>
                      <Chip
                        label={files.length}
                        size="small"
                        sx={{
                          bgcolor: alpha(theme.palette.info.main, 0.1),
                          color: theme.palette.info.main,
                          fontWeight: 500,
                        }}
                      />
                    </Box>
                  )}
                  <Grid container spacing={2.5}>
                    {files.map((file, index) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={file.id}>
                        <KnowledgehubFileCard
                          file={file}
                          index={index}
                          folderCount={folders.length}
                          isHovered={hoveredItemId === file.id}
                          onMouseEnter={() => setHoveredItemId(file.id)}
                          onMouseLeave={() => setHoveredItemId(null)}
                          onClick={() => setViewerFile(file)}
                          onDelete={handleDeleteFile}
                          onReupload={handleReuploadFile}
                          onDownload={handleDownloadFile}
                          onEdit={handleEditFile}
                          onArchive={handleArchiveFile}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}
            </>
          )}
          {/* Loader */}
          <Loader isLoading={loading} />
        </Box>
      )}

      {/* File Viewer Modal */}
      <KnowledgehubFileViewer
        file={viewerFile}
        onClose={() => setViewerFile(null)}
      />

      {/* Upload File Modal */}
      {uploadTargetFolder && (
        <KnowledgehubUploadFileModal
          open={!!uploadTargetFolder}
          repositoryId={uploadTargetFolder.id}
          repositoryName={uploadTargetFolder.name}
          onClose={() => setUploadTargetFolder(null)}
          onSubmit={handleUploadFile}
        />
      )}

      {/* Archive Drawer */}
      <KnowledgehubArchiveDrawer
        open={archiveDrawerOpen}
        archivedFiles={archivedFiles}
        onClose={() => setArchiveDrawerOpen(false)}
        onDownload={handleDownloadArchivedFile}
      />

      {/* Edit Drawer */}
      <KnowledgehubEditDrawer
        target={editTarget}
        onClose={() => setEditTarget(null)}
        onSave={handleEditSave}
      />

      {/* Create Repo Modal */}
      <KnowledgehubCreateRepoModal
        open={isCreateModalOpen}
        parentId={currentFolder?.id ?? null}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateRepo}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          {deleteTarget?.type === 'folder' ? (
            <>
              Are you sure you want to delete this repository "
              {(deleteTarget.data as KnowledgehubFolderResponse).name}"?
              <br />
              This action will permanently delete all sub-folders and files inside it.
            </>
          ) : (
            <>
              Are you sure you want to delete the file "
              {(deleteTarget?.data as KnowledgehubFileResponse)?.title}"?
              <br />
              This action will permanently delete the file content.
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
